/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { SendMessageSchema, type SendMessageInput } from '@therapy/shared-validators';
import { Send } from 'lucide-react';
import { type SyntheticEvent, useCallback, useEffect, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useSendMessage, useThread } from '../../lib/queries/messaging.js';
import { cn } from '../../lib/utils/cn.js';
import { formatIst } from '../../lib/utils/datetime.js';

interface ChatPaneProps {
  readonly threadId: string;
  readonly meId: string;
}

/** Right pane: a conversation's messages + composer (Figma Messages). */
export function ChatPane({ threadId, meId }: ChatPaneProps): JSX.Element {
  const { t } = useTranslation();
  const thread = useThread(threadId);
  const send = useSendMessage();
  const { register, handleSubmit, reset, formState } = useForm<SendMessageInput>({
    resolver: zodResolver(SendMessageSchema),
  });

  useEffect(() => {
    reset();
  }, [threadId, reset]);

  const submit = useCallback(
    async (values: SendMessageInput): Promise<void> => {
      await send.mutateAsync({ threadId, body: values.body });
      reset();
    },
    [send, threadId, reset],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <div className="flex flex-1 flex-col">
      <div className="border-b border-slate-200 p-4 dark:border-slate-700">
        <p className="font-semibold text-slate-800 dark:text-white">
          {thread.data?.otherUserName ?? t('messages.conversation')}
        </p>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <QueryState
          isLoading={thread.isLoading}
          isError={thread.isError}
          isEmpty={(thread.data?.messages ?? []).length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('messages.empty')}
        >
          {(thread.data?.messages ?? []).map((message) => {
            const mine = message.senderUserId === meId;
            return (
              <div key={message.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[75%] rounded-2xl px-4 py-2 text-sm',
                    mine
                      ? 'bg-[var(--accent)] text-white'
                      : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                  )}
                >
                  <p className="whitespace-pre-wrap break-words">{message.body}</p>
                  <p
                    className={cn(
                      'mt-1 text-right text-[10px]',
                      mine ? 'text-white/70' : 'text-slate-400',
                    )}
                  >
                    {formatIst(message.createdAt, 'd MMM, h:mm a')}
                  </p>
                </div>
              </div>
            );
          })}
        </QueryState>
      </div>

      <form
        onSubmit={onFormSubmit}
        className="flex items-center gap-2 border-t border-slate-200 p-3 dark:border-slate-700"
        noValidate
      >
        <input
          type="text"
          placeholder={t('messages.composer')}
          aria-label={t('messages.composer')}
          aria-invalid={formState.errors.body !== undefined}
          className="focus-ring h-11 flex-1 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          {...register('body')}
        />
        <Button type="submit" size="icon" disabled={formState.isSubmitting || send.isPending}>
          <Send className="h-4 w-4" aria-hidden="true" />
        </Button>
      </form>
    </div>
  );
}
